import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function ProgramYearSemesterMapping() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    const value = sessionStorage.getItem("user");
    return value ? JSON.parse(value) : null;
  }, []);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState("");
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState("");
  const [rowsAcademicYear, setRowsAcademicYear] = useState({});
  const [programFormData, setProgramFormData] = useState({
    program_name: "",
    program_desc: "",
    program_duration: "",
    program_start_year: "",
    is_program_active: 1,
  });
  const [notice, setNotice] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const headers = useMemo(() => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  const showNotice = (message, severity = "success") =>
    setNotice({ open: true, message, severity });

  const loadPrograms = useCallback(async () => {
    if (!user?.client_id || !user?.college_id) {
      showNotice("Valid user and college are required", "error");
      return;
    }
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}`,
        { headers }
      );
      const items = Array.isArray(data) ? data : data?.data || [];
      setPrograms(Array.isArray(items) ? items : []);
    } catch (error) {
      showNotice(error.response?.data?.message || "Failed to load programs", "error");
    }
  }, [API_BASE_URL, headers, user]);
  const loadAcademicYears = useCallback(async () => {
    if (!user?.client_id || !user?.college_id) {
      setAcademicYears([]);
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${user.college_id}`,
        { headers }
      );

      const items = Array.isArray(data) ? data : data?.data?.items || data?.data || [];

      setAcademicYears(Array.isArray(items) ? items : []);
    } catch (error) {
      setAcademicYears([]);
      showNotice(error.response?.data?.message || "Failed to load academic years", "error");
    }
  }, [API_BASE_URL, headers, user]);

  const loadMappings = useCallback(async () => {
    if (!user?.client_id || !user?.college_id) {
      setMappings([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/program-year-semesters/${user.client_id}/${user.college_id}`,
        { headers }
      );
      const items = data?.data?.items;
      setMappings(Array.isArray(items) ? items : []);
    } catch (error) {
      setMappings([]);
      showNotice(error.response?.data?.message || "Failed to load mappings", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, headers, user]);

  const selectedProgram = programs.find(
    (program) => String(program.program_id) === String(selectedProgramId)
  );

  const resetProgramForm = useCallback(() => {
    setProgramFormData({
      program_name: "",
      program_desc: "",
      program_duration: "",
      program_start_year: "",
      is_program_active: 1,
    });
  }, []);
  useEffect(() => {
    loadPrograms();
    loadMappings();
    loadAcademicYears();
  }, [loadPrograms, loadMappings, loadAcademicYears]);
  const handleCreateProgram = async () => {
    if (!user?.client_id || !user?.college_id) {
      showNotice("Valid user and college are required", "error");
      return;
    }
    if (!String(programFormData.program_name || "").trim()) {
      showNotice("Program name is required", "error");
      return;
    }
    if (!programFormData.program_duration || Number(programFormData.program_duration) <= 0) {
      showNotice("Program duration is required", "error");
      return;
    }
    if (!programFormData.program_start_year || Number(programFormData.program_start_year) <= 0) {
      showNotice("Program start year is required", "error");
      return;
    }

    try {
      const payload = {
        client_id: user.client_id,
        college_id: user.college_id,
        program_name: programFormData.program_name,
        program_desc: programFormData.program_desc,
        program_duration: Number(programFormData.program_duration),
        program_start_year: Number(programFormData.program_start_year),
        is_program_active: Number(programFormData.is_program_active ?? 1),
        created_by: user.user_email || user.user_email_id || "system",
      };

      const { data } = await axios.post(`${API_BASE_URL}/api/programs`, payload, { headers });
      const createdProgram = data?.data || null;
      setProgramDialogOpen(false);
      resetProgramForm();
      await loadPrograms();
      await loadMappings();
      if (createdProgram?.program_id) {
        setSelectedProgramId(String(createdProgram.program_id));
      }
      showNotice(data?.message || "Program created successfully");
    } catch (error) {
      showNotice(error.response?.data?.message || "Failed to create program", "error");
    }
  };

  const rows = useMemo(() => {
    if (!selectedProgram) return [];
    return [selectedProgram].flatMap((program) => {
      const duration = Number(program.program_duration);
      const totalSemesters = duration * 2;
      if (!Number.isFinite(duration) || duration <= 0 || !Number.isInteger(totalSemesters)) {
        return [];
      }
      return Array.from({ length: totalSemesters }, (_, index) => {
        const yearNumber = Math.floor(index / 2) + 1;
        const semesterNumber = index + 1;
        const mapping = mappings.find(
          (item) =>
            Number(item.program_id) === Number(program.program_id) &&
            Number(item.year_number) === yearNumber &&
            Number(item.semester_number) === semesterNumber
        );
        return {
          id: `${program.program_id}_${yearNumber}_${semesterNumber}`,
          program_id: program.program_id,
          program_name: program.program_name,
          year_number: yearNumber,
          semester_number: semesterNumber,
          mapped: Boolean(mapping),
          mapping_id: mapping?.program_year_semester_id || null,
          acyear_id: mapping?.acyear_id ? Number(mapping.acyear_id) : "",
          academic_year: mapping?.academic_year || "",
        };
      });
    });
  }, [mappings, selectedProgram]);

  const programRows = useMemo(
    () =>
      programs.map((program) => ({
        id: program.program_id,
        program_id: program.program_id,
        program_name: program.program_name,
        program_duration: Number(program.program_duration || 0),
        total_semesters: Math.round(Number(program.program_duration || 0) * 2),
      })),
    [programs]
  );

  const handleToggle = async (row) => {
    if (updatingRowId) return;
    const rowProgram = programs.find(
      (program) => Number(program.program_id) === Number(row.program_id)
    );
    if (!user?.client_id || !user?.college_id || !rowProgram) {
      showNotice("Valid user, college, and program are required", "error");
      return;
    }
    if (!Number.isInteger(row.year_number) || !Number.isInteger(row.semester_number)) {
      showNotice("Invalid mapping selection", "error");
      return;
    }
    const selectedAcademicYearId = rowsAcademicYear[row.id] || row.acyear_id;

    if (!selectedAcademicYearId) {
      showNotice("Please select an academic year for this semester", "error");
      return;
    }
    if (row.mapped && !row.mapping_id) {
      showNotice("Mapping ID is missing; refresh and try again", "error");
      return;
    }
    setUpdatingRowId(row.id);
    try {
      if (!row.mapped) {
        await axios.post(
          `${API_BASE_URL}/api/program-year-semesters/${user.client_id}/${user.college_id}`,
          {
            program_id: row.program_id,
            year_number: row.year_number,
            semester_number: row.semester_number,
            acyear_id: Number(selectedAcademicYearId),
            created_by: user.user_email || user.user_email_id || "system",
          },
          { headers }
        );
        showNotice("Mapped successfully");
      } else {
        await axios.delete(
          `${API_BASE_URL}/api/program-year-semesters/${user.client_id}/${user.college_id}/${row.mapping_id}`,
          { headers }
        );
        showNotice("Unmapped successfully");
      }
      await loadMappings();
    } catch (error) {
      showNotice(error.response?.data?.message || "Failed to update mapping", "error");
    } finally {
      setUpdatingRowId("");
    }
  };

  const programColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "program_name", headerName: "Program", flex: 1, minWidth: 260 },
    {
      field: "program_duration",
      headerName: "Duration",
      width: 160,
      valueFormatter: ({ value }) => (value > 0 ? `${value} Years` : "Not configured"),
    },
    {
      field: "academic_year_mapping",
      headerName: "Academic Year Mapping",
      minWidth: 300,
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const programMappings = mappings.filter(
          (mapping) => String(mapping.program_id) === String(params.row.program_id)
        );

        const groupedMappings = programMappings.reduce((acc, mapping) => {
          const academicYear = mapping.academic_year || "Year Not Set";

          if (!acc[academicYear]) {
            acc[academicYear] = [];
          }

          acc[academicYear].push(Number(mapping.semester_number));

          return acc;
        }, {});

        const academicYears = Object.entries(groupedMappings).sort(([yearA], [yearB]) =>
          yearA.localeCompare(yearB)
        );

        if (academicYears.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not mapped
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              py: 1,
              width: "100%",
            }}
          >
            {academicYears.map(([academicYear, semesters]) => (
              <Box
                key={academicYear}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: "#f5f8ff",
                  border: "1px solid #dbe5f5",
                }}
              >
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {academicYear}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Semesters: {[...new Set(semesters)].sort((a, b) => a - b).join(", ")}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    { field: "total_semesters", headerName: "Semesters", width: 140 },
  ];

  const semesterColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
      valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    { field: "year_number", headerName: "Year", width: 140 },
    { field: "semester_number", headerName: "Semester", width: 160 },
    {
      field: "acyear_id",
      headerName: "Academic Year",
      width: 220,
      sortable: false,
      renderCell: ({ row }) => (
        <TextField
          select
          size="small"
          fullWidth
          value={rowsAcademicYear[row.id] ?? row.acyear_id ?? ""}
          disabled={Boolean(updatingRowId) || loading}
          SelectProps={{ native: true }}
          onChange={(event) =>
            setRowsAcademicYear((prev) => ({
              ...prev,
              [row.id]: event.target.value,
            }))
          }
        >
          <option value="">Select Academic Year</option>
          {academicYears.map((year) => (
            <option key={year.acyear_id} value={String(year.acyear_id)}>
              {year.academic_year}
            </option>
          ))}
        </TextField>
      ),
    },
    {
      field: "mapped",
      headerName: "Map/Unmap",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => (
        <FormControlLabel
          control={
            <Switch
              checked={row.mapped}
              disabled={Boolean(updatingRowId) || loading}
              onChange={() => handleToggle(row)}
            />
          }
          label={row.mapped ? "Mapped" : "Unmapped"}
        />
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar
          sx={{
            gap: { xs: 1.5, sm: 2 },
            px: { xs: 1, sm: 2 },
            py: { xs: 1.5, sm: 1 },
            flexWrap: "wrap",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              width: { xs: "100%", sm: "auto" },
              fontSize: { xs: "1rem", sm: "1.25rem" },
              textAlign: { xs: "center", sm: "left" },
              lineHeight: 1.4,
              overflowWrap: "anywhere",
            }}
          >
            PROGRAM-YEAR SEMESTER MAPPING
          </Typography>

          <TextField
            select
            size="medium"
            label="Select Program"
            name="program"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
            value={selectedProgramId}
            onChange={(event) => {
              setSelectedProgramId(event.target.value);
            }}
            disabled={loading || Boolean(updatingRowId)}
            sx={{
              width: { xs: "100%", sm: 280, md: 320 },
              minWidth: 0,
              "& .MuiInputBase-root": {
                minHeight: 48,
              },
            }}
          >
            <option value="">Select Program</option>
            {programs.map((program) => {
              const duration = Number(program.program_duration);
              const validDuration =
                Number.isFinite(duration) && duration > 0 && Number.isInteger(duration * 2);

              return (
                <option
                  key={program.program_id}
                  value={String(program.program_id)}
                  disabled={!validDuration}
                >
                  {program.program_name}{" "}
                  {validDuration ? `(${duration} Years)` : "(Duration not configured)"}
                </option>
              );
            })}
          </TextField>
        </Toolbar>

        {selectedProgramId !== "all" && selectedProgram && (
          <Box px={3} pb={1}>
            <Typography variant="body2" color="text.secondary">
              Duration: {Number(selectedProgram.program_duration || 0)} years · Expected mappings:{" "}
              {Math.round(Number(selectedProgram.program_duration || 0) * 2)} semesters
            </Typography>
          </Box>
        )}

        <Box
          ml={2}
          mr={2}
          sx={{
            height: 560,
          }}
        >
          <DataGrid
            rows={programRows}
            columns={programColumns}
            loading={loading}
            getRowId={(row) => row.id}
            getRowHeight={() => "auto"}
            pagination
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            onRowClick={({ row }) => setSelectedProgramId(String(row.program_id))}
            sx={{
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Box>

        <Dialog
          open={Boolean(selectedProgram)}
          onClose={() => setSelectedProgramId("")}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ pr: 6 }}>
            {selectedProgram?.program_name || "Program"} - Semester Mapping
            <IconButton
              aria-label="Close"
              onClick={() => setSelectedProgramId("")}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box px={3} pt={2} pb={1}>
              <Typography variant="body2" color="text.secondary">
                Click the switches below to map or unmap the {rows.length} semesters.
              </Typography>
            </Box>

            <Box sx={{ height: 570, width: "100%" }}>
              <DataGrid
                rows={rows}
                columns={semesterColumns}
                loading={loading}
                getRowId={(row) => row.id}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#f5f5f5",
                    borderBottom: "2px solid #e0e0e0",
                  },
                }}
              />
            </Box>
          </DialogContent>
        </Dialog>
      </Paper>

      <Dialog
        open={programDialogOpen}
        onClose={() => {
          setProgramDialogOpen(false);
          resetProgramForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Program</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
            <TextField
              label="Program Name"
              value={programFormData.program_name}
              onChange={(event) =>
                setProgramFormData((prev) => ({ ...prev, program_name: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={programFormData.program_desc}
              onChange={(event) =>
                setProgramFormData((prev) => ({ ...prev, program_desc: event.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 2 }}>
              <TextField
                label="Duration (Years)"
                type="number"
                value={programFormData.program_duration}
                onChange={(event) =>
                  setProgramFormData((prev) => ({
                    ...prev,
                    program_duration: event.target.value,
                  }))
                }
                fullWidth
                inputProps={{ min: 1, step: 1 }}
              />
              <TextField
                label="Program Start Year"
                type="number"
                value={programFormData.program_start_year}
                onChange={(event) =>
                  setProgramFormData((prev) => ({
                    ...prev,
                    program_start_year: event.target.value,
                  }))
                }
                fullWidth
                inputProps={{ min: 2000, step: 1 }}
              />
            </Box>
            <TextField
              select
              label="Status"
              value={programFormData.is_program_active}
              onChange={(event) =>
                setProgramFormData((prev) => ({
                  ...prev,
                  is_program_active: Number(event.target.value),
                }))
              }
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setProgramDialogOpen(false);
              resetProgramForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProgram}
            sx={{ color: "#fff", "&:hover": { color: "#fff" } }}
          >
            Save Program
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={() => setNotice({ ...notice, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={notice.severity}
          onClose={() => setNotice({ ...notice, open: false })}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
